import React from 'react';
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from 'expo-linear-gradient';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';

interface RecipeBottomBarProps {
  servings: number;
  servingsDisplay?: string;
  onServingsChange: (delta: number) => void;
  onAsk?: () => void;
  onCook?: () => void;
  minServings?: number;
  maxServings?: number;
}

const isDescriptiveYield = (display: string) =>
  /[a-zA-ZÀ-ÿ]{3,}/.test(display);

export const RecipeBottomBar: React.FC<RecipeBottomBarProps> = ({
  servings,
  servingsDisplay,
  onServingsChange,
  onAsk,
  onCook,
  minServings = 1,
  maxServings = 20,
}) => {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();

  const canDecrease = servings > minServings;
  const canIncrease = servings < maxServings;

  return (
    <View style={styles.wrapper}>
      {/* Gradient fade at top */}
      <LinearGradient
        colors={['transparent', Paprika.background]}
        style={styles.gradient}
      />

      <View
        style={[
          styles.container,
          { paddingBottom: Math.max(insets.bottom, PaprikaSpacing.md) },
        ]}
      >
        {/* Servings Selector */}
        <View style={styles.servingsContainer}>
          <TouchableOpacity
            style={[styles.servingsButton, !canDecrease && styles.servingsButtonDisabled]}
            onPress={() => canDecrease && onServingsChange(-1)}
            disabled={!canDecrease}
            activeOpacity={0.7}
          >
            <Feather
              name="minus"
              size={18}
              color={canDecrease ? Paprika.textPrimary : Paprika.textMuted}
            />
          </TouchableOpacity>

          <Text style={styles.servingsText}>
            {servingsDisplay && isDescriptiveYield(servingsDisplay)
              ? servingsDisplay
              : t('recipes.cookingFor', { count: servings })}
          </Text>

          <TouchableOpacity
            style={[styles.servingsButton, !canIncrease && styles.servingsButtonDisabled]}
            onPress={() => canIncrease && onServingsChange(1)}
            disabled={!canIncrease}
            activeOpacity={0.7}
          >
            <Feather
              name="plus"
              size={18}
              color={canIncrease ? Paprika.textPrimary : Paprika.textMuted}
            />
          </TouchableOpacity>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionsContainer}>
          {onAsk && (
            <TouchableOpacity
              style={styles.askButton}
              onPress={onAsk}
              activeOpacity={0.7}
            >
              <Feather name="message-circle" size={18} color={Paprika.textPrimary} />
              <Text style={styles.askButtonText}>{t('recipes.ask')}</Text>
            </TouchableOpacity>
          )}

          {onCook && (
            <TouchableOpacity
              style={styles.cookButton}
              onPress={onCook}
              activeOpacity={0.8}
            >
              <Text style={styles.cookButtonText}>{t('recipes.cook')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  gradient: {
    height: 24,
  },
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.lg,
    backgroundColor: Paprika.background,
  },
  servingsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  servingsButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Paprika.outlineVariant + '33',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.background,
  },
  servingsButtonDisabled: {
    borderColor: Paprika.outlineVariant + '1A',
    backgroundColor: Paprika.surfaceLow,
  },
  servingsText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textPrimary,
    minWidth: 100,
    textAlign: 'center',
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  askButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    borderWidth: 1,
    borderColor: Paprika.outlineVariant + '33',
    borderRadius: PaprikaRadius.full,
    gap: 6,
  },
  askButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textPrimary,
  },
  cookButton: {
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.xl,
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.full,
  },
  cookButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.background,
  },
});

export default RecipeBottomBar;
